import { inject, Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

//Servicios
import { TokenReaderService } from '@/app/services/security/token-reader.service';
import { LogoutService } from '@/app/services/security/logout.service';

//Librerías externas
import { AuthService } from '@auth0/auth0-angular';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService implements CanActivate {
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private tokenReader = inject(TokenReaderService);
  private auth0 = inject(AuthService);
  private logoutService = inject(LogoutService);

  constructor() {}

  /**
   * Protege rutas autenticadas. Verifica la sesión de Auth0 y luego asegura que
   * el usuario exista en la base de datos (getUserInfo). Aplica además la regla
   * de que el rol de solo reportes (idRol === 5) únicamente accede a /reportes.
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.auth0.isAuthenticated$.pipe(
      take(1),
      switchMap((isAuthenticated) => {
        if (!isAuthenticated) {
          this.toastr.error('Tu sesión expiró o no tienes permiso para ver esta página.', 'Acceso no autorizado');
          this.router.navigate(['/login']); 
          return of(false);
        }

        return this.tokenReader.ensureUserLoaded().pipe(
          map((user) => {
            if (!user) {
              this.toastr.error('Tu usuario no existe en la base de datos.', 'Acceso denegado');
              this.logoutService.cerrarSesion();
              return false;
            }

            // El rol de solo reportes únicamente accede a /reportes.
            if (Number(user.idRol) === 5 && route.routeConfig?.path !== 'reportes') {
              this.toastr.error('Solo tienes acceso a reportes.', 'Acceso restringido');
              this.router.navigate(['/reportes']);
              return false;
            }

            return true;
          }),
          catchError(() => {
            this.toastr.error('Tu usuario no existe en la base de datos.', 'Acceso denegado');
            this.logoutService.cerrarSesion();
            return of(false);
          })
        );
      })
    );
  }
}
